import { Component, Input, OnInit, ElementRef, Injector } from '@angular/core';
import { environment } from '../../../../../../environments/environment';
import { WidgetVirtualBase } from '../../widgetVirtualBase';
import { HRentrollWidgetPageData } from './hrentroll-widget-page-data';
import { HRentrollHeights } from './models/hrentroll-heights';
import { HRentrollRowEntry } from './models/hrentroll-row-entry';

/**
 * HRentRoll Widget
 */
@Component({
  selector: 'hrentroll-widget',
  templateUrl: './hrentroll-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../../common/css/table-widgets.scss', './hrentroll-widget.component.scss']
})
export class HRentRollWidgetComponent extends WidgetVirtualBase implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Underline the absorbed (red) and reabsorb (yellow) tenants
   */
  public debugReabsorb = true;

  /**
   * Constructor
   * @param injector
   * @param elementRef
   */
  constructor(injector: Injector, private elementRef: ElementRef) {
    super(injector, 'hRentRoll');
  }

  /**
   * Log messages in DEV environment
   */
  log(message: string) {
    if (!environment.production) {
      console.log(`${this.name}: ${message}`);
    }
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.executeWidgetDataLoadedInWidgetClass = true;
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index {number} Index
   * @param el {any} Element
   */
  trackById(_index: number, el: any): number {
    return el.trackId;
  }

  /**
   * Register Master widget events
   */
  registerEventsMaster(): void {
    this.useReadOnlySaveChangesHandler();
  }

  /**
   * Register Slave widgets events
   */
  registerEventsSlave(): void {
    this.useReadOnlySaveChangesHandler();
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   * This function is called for Master widget
   */
  onInitializedMaster(): void {
    this.currentLayout = this.widgetConfig.widgetSettings.currentLayout || "Default";
    this.widgetConfig.widgetSettings.currentLayout = this.currentLayout;

    this.mockupData$ = this.widgetService.loadMockup('hrentroll');

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('hrentroll', this.getReport());
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   * This function is called for Slave widgets
   */
  onInitializedSlave(): void {
    //slave page
    this.widgetData = new HRentrollWidgetPageData(this);
    this.widgetData.pageNo = this.widgetConfig.widgetSettings.slavePageNo;

    const intervalId = setInterval(() => {
      if (!this.widgetData.slaveDataUpdated && !this.destroyed) {
        const pageIndex: number = this.widgetData.pageNo;
        const slavePageData = this.virtualPagesService.getPageData(this.WIDGET_MASTER_SLAVE_MSG, pageIndex);
        //if data exists set it to virtual page
        if (slavePageData) {
          !environment.production && console.log(`Returning slave page ${pageIndex} from page cache`);
          slavePageData.slaveDataUpdated = true;
          this.eventBus.broadcastEvent(this.WIDGET_MASTER_SLAVE_MSG, { cmd: "setSlaveData", value: slavePageData });
        }
      } else {
        clearInterval(intervalId);
      }
    }, 20);
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    this.log('preprocessLoadedData');
    this.log(`isMasterWidget: ${this.isMasterWidget}`);

    // we will check uppercase because it might be lower/upper/mixed case
    const LET = 'LET';

    //initialy render all data so we can calculate which rows go on which page
    const processedData: any = {
      reportId: data.reportId,
      rowsLet: data.supportHRentRoll.filter(o => o.lettingStatusId?.trim().toUpperCase() === LET),
      rowsVacant: data.supportHRentRoll.filter(o => o.lettingStatusId?.trim().toUpperCase() !== LET),
      displayLetSubtotal: true,
      displayLetSubtotalSpace: true,
      displayVacantSubtotal: true,
      displayVacantSubtotalSpace: true,
      displayTotal: true,
      tenantLeasingCommissionsMonthsLabelOverride: '',
      tenantTenantImprovementsPerSqmLabelOverride: '',
      displayValuesOverTimeId: data.displayValuesOverTimeId
    };

    //Task 20588: Contractual rent for Vacant areas - totals => vacant rows contractual rent should be empty
    processedData.rowsVacant.forEach(vacantRow => {
      vacantRow.contractualRent = null;
      vacantRow.contractualRentPerArea = null;
    });

    //Label overrides from MLA possible
    if (data.supportHRentRoll.length) {
      if (data.supportHRentRoll[0].tenantLeasingCommissionsMonthsLabelOverride) {
        processedData.tenantLeasingCommissionsMonthsLabelOverride = data.supportHRentRoll[0].tenantLeasingCommissionsMonthsLabelOverride;
      }
      if (data.supportHRentRoll[0].tenantTenantImprovementsPerSqmLabelOverride) {
        processedData.tenantTenantImprovementsPerSqmLabelOverride = data.supportHRentRoll[0].tenantTenantImprovementsPerSqmLabelOverride;
      }
    }

    //used for angular ngFor
    processedData.rowsLet.forEach((o, i) => o.trackId = i);
    processedData.rowsVacant.forEach((o, i) => o.trackId = i);

    setTimeout(() => {
      this.setupWidgetViewModel();
    });

    return processedData;
  }

  /**
   * Setup widget ViewModel
   */
  setupWidgetViewModel(): void {
    if (!this.isMasterWidget) {
      return;
    }

    if (this.destroyed) {
      //if widget is destroyed => cancel calculation
      return;
    }

    if (!this.page || this.page.options.isHidden) {
      //page is hidden so we can't calculate
      return;
    }

    const RETRY_TIMEOUT = 50;

    const widgetEl: HTMLElement = this.elementRef.nativeElement;
    const widgetDivEl: HTMLElement = widgetEl.querySelector('.rd-widget');
    if (widgetDivEl === null) {
      this.log('calculatePages: no widgetData yet.. retry');
      setTimeout(() => this.setupWidgetViewModel(), RETRY_TIMEOUT);
      return;
    }

    this.log('calculatePages: widgetData is here.. widget started drawing..');

    const rowsNodeList = widgetEl.querySelectorAll('table.widget-HRentRollScenario-content tr.hrentrollLine');
    const htmlRows = Array.from(rowsNodeList);
    this.log(`HTML rows: ${htmlRows.length}`);
    const dataRowsCount = this.widgetData.rowsLet.length + this.widgetData.rowsVacant.length;
    this.log(`Data rows: ${dataRowsCount}`);
    if (htmlRows.length !== dataRowsCount) {
      this.log('calculatePages: Not all rows displayed.. retry');
      setTimeout(() => this.setupWidgetViewModel(), RETRY_TIMEOUT);
      return;
    }

    this.log('calculatePages: All rows displayed.. let\'s calculate');
    const magicAdjustmentInPixels = 5;

    const wholePageEl: HTMLElement = widgetEl.closest('.gridster-container');
    const firstPageWidgetEl: HTMLElement = widgetEl.closest('.gridster-item');//can be smaller than whole page (to allow widget line on the top)
    //const boxContentEl = widgetEl.closest('.box-content') as HTMLElement;
    const firstPageHeight = firstPageWidgetEl.offsetHeight - magicAdjustmentInPixels;
    const otherPagesHeight = wholePageEl.offsetHeight - magicAdjustmentInPixels;
    this.log(`calculatePages: First page height: ${firstPageHeight}, : Other pages height: ${otherPagesHeight}`);

    //calculate fixed top and bottom (table header and info message)
    const fixedTopElement: HTMLElement = widgetEl.querySelector('.widget-HRentRollScenario-fixed-top');
    const fixedBottomElement: HTMLElement = widgetEl.querySelector('.widget-HRentRollScenario-fixed-bottom');

    const fixedTopHeight = fixedTopElement.offsetHeight;
    const fixedBottomHeight = fixedBottomElement.offsetHeight;
    this.log(`calculatePages: Fixed top: ${fixedTopHeight}`);//should be 76
    this.log(`calculatePages: Fixed bottom: ${fixedBottomHeight}`);//should be 23

    if (fixedTopHeight <= 55 || fixedBottomHeight <= 10) {
      this.log('calculatePages: Probably translations not yet set.. retry');
      setTimeout(() => this.setupWidgetViewModel(), RETRY_TIMEOUT);
      return;
    }

    this.log(`calculatePages: All drawn.. let's calculate'`);

    const widgetRows: HRentrollRowEntry[] = Array.from(widgetEl.querySelectorAll('table.widget-HRentRollScenario-content tr:not(.widget-ruller-large)')).map((rowEl) => {
      return {
        rowEl,
        height: (rowEl as HTMLElement).offsetHeight,
        classes: (rowEl as HTMLElement).classList
      };
    });

    const fixedSpaceTakenHeight = fixedTopHeight + fixedBottomHeight;//on every page we have upper header and bottom footnote
    this.log(`calculatePages: Fixed space height: ${fixedSpaceTakenHeight}`);

    /* Example
      hRentRoll: Fixed top: 76
      hRentRoll: Fixed bottom: 23
    */

    const rowsLet = [...this.widgetData.rowsLet];
    const rowsVacant = [...this.widgetData.rowsVacant];

    const currentPageData = new HRentrollWidgetPageData(this, this.widgetData);
    this.widgetPages.length = 0;
    this.widgetPages.push(currentPageData);

    this.widgetData = currentPageData;

    const heights: HRentrollHeights = {
      firstPageHeight,
      fixedSpaceTakenHeight,
      fixedBottomHeight,
      otherPagesHeight
    };

    this.handleRows(widgetRows, currentPageData, rowsLet, rowsVacant, heights);

    //repaint
    this.widgetData = null;
    setTimeout(() => {
      this.widgetData = this.widgetPages[0];//set own (master) page view data

      //save pages for slave pages (print!)
      this.virtualPagesService.setPagesData(this.WIDGET_MASTER_SLAVE_MSG, this.widgetPages);

      //create slave pages if required
      this.processWidgetSlavePages(this);
      //IMPORTANT: call widgetDataLoaded after slave pages are added to document, so documentDataLoaded is not fired too soon!!!
      this.page.widgetDataLoaded(this.widgetConfig.widgetSettings.id);
    });
  }

  /**
   * Handle setting up all rows
   * @param widgetRows
   * @param currentPageData
   * @param rowsLet
   * @param rowsVacant
   * @param heights
   */
  private handleRows(widgetRows: HRentrollRowEntry[], currentPageData: HRentrollWidgetPageData, rowsLet: any[], rowsVacant: any[], heights: HRentrollHeights) {
    const firstPageHeight = heights.firstPageHeight;
    const fixedSpaceTakenHeight = heights.fixedSpaceTakenHeight;
    const fixedBottomHeight = heights.fixedBottomHeight;
    const otherPagesHeight = heights.otherPagesHeight;

    let pageIndex = 0;
    let remainingContent = firstPageHeight - fixedSpaceTakenHeight;
    for (const rowEntry of widgetRows) {
      if (remainingContent < rowEntry.height) {
        //no more space, goes on next page
        //for current page set footnote padding (position absolute, bottom: 0 does not work in pdf print)
        if (remainingContent > fixedBottomHeight) {
          currentPageData.setFootnotePaddingTop(pageIndex, remainingContent);
        }

        remainingContent = otherPagesHeight - fixedSpaceTakenHeight;
        pageIndex = currentPageData.createNewPage();
      }

      remainingContent -= rowEntry.height;
      remainingContent = this.handleRow(rowEntry, currentPageData, rowsLet, rowsVacant, pageIndex, remainingContent);
    }
  }

  /**
   * Handle setting up a row
   * @param rowEntry
   * @param currentPageData
   * @param rowsLet
   * @param rowsVacant
   * @param pageIndex
   * @param remainingContent
   * @returns
   */
  private handleRow(rowEntry: HRentrollRowEntry, currentPageData: HRentrollWidgetPageData, rowsLet: any[], rowsVacant: any[], pageIndex: number, remainingContent: number) {
    if (rowEntry.classes.contains('hrentrollLineLet')) {
      const row = rowsLet.shift(); //get first and remove from array
      currentPageData.addLetDataRow(pageIndex, row);
    } else if (rowEntry.classes.contains('hrentrollLineVacant')) {
      const row = rowsVacant.shift(); //get first and remove from array
      currentPageData.addVacantDataRow(pageIndex, row);
    } else if (rowEntry.classes.contains('displayLetSubtotal')) {
      currentPageData.addLetTotal(pageIndex);
    } else if (rowEntry.classes.contains('displayLetSubtotalSpace')) {
      currentPageData.addLetTotalSpace(pageIndex);
      if (!currentPageData.displayLetSubtotalSpace) {
        remainingContent += rowEntry.height; //if no need for display reclaim content space
      }
    } else if (rowEntry.classes.contains('displayVacantSubtotal')) {
      currentPageData.addVacantTotal(pageIndex);
    } else if (rowEntry.classes.contains('displayVacantSubtotalSpace')) {
      currentPageData.addVacantTotalSpace(pageIndex);
      if (!currentPageData.displayVacantSubtotalSpace) {
        remainingContent += rowEntry.height; //if no need for display reclaim content space
      }
    } else if (rowEntry.classes.contains('displayTotal')) {
      currentPageData.addTotal(pageIndex);
    } else if (!environment.production) {
      alert('Unknown row type!!');
    }

    return remainingContent;
  }
}
